import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Loader2, Maximize2, Send, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AttachmentTypePicker, type AttachmentOption } from "@/components/chat/AttachmentTypePicker";
import { MediaLightbox, type LightboxMedia } from "@/components/chat/MediaLightbox";
import { useChatAttachment } from "@/lib/useChatAttachment";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import { useRealtimeTable } from "@/lib/dbHooks";
import { useChatSnapshot } from "@/lib/useChatSnapshot";
import { requestPersistentStorage } from "@/lib/persistentStorage";
import { supabase } from "@/lib/supabase";

type Reaction = { emoji: string; by: string[] };
type AttachmentMedia =
  | { kind: "image"; url: string; mime: string; width?: number; height?: number; alt?: string }
  | { kind: "video"; url: string; mime: string; durationSec?: number }
  | { kind: "gif";   url: string; mime: string };
type Message = {
  id: string;
  from: string;
  body?: string;
  attachment?: AttachmentMedia;
  voice?: { seconds: number };
  time: string;
  status?: "sending" | "delivered" | "read" | "failed";
  reactions?: Reaction[];
};

const REACTION_CHOICES = ["❤️", "😊", "😂", "👍", "🥰", "😢"];

// Storage TTL for batch-signed chat attachment URLs. One hour matches
// the cache lifetime the rest of the app uses; the URLs are minted on
// every refresh of the message list, so keeping this short is fine.
const CHAT_SIGNED_URL_TTL_SECONDS = 60 * 60;

function labelForKind(kind: "image" | "video" | "gif"): string {
  switch (kind) {
    case "image": return "壓縮圖片";
    case "video": return "處理影片";
    case "gif":   return "處理 GIF";
  }
}

export function FamilyChatView() {
  const me = useCurrentUser();
  const userId = me.user?.id ?? null;
  const cf = useCurrentFamily(userId);

  const [draft, setDraft] = useState("");
  const [showReactionsFor, setShowReactionsFor] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  // Tracks which attachment is open in the fullscreen lightbox.
  // `null` ⇒ no overlay. The overlay itself owns its AnimatePresence
  // exit transition so the FamilyChatView render path stays a flat
  // list of JSX nodes.
  const [lightbox, setLightbox] = useState<LightboxMedia | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const threadId = cf.thread?.id ?? null;

  // Hook: attachment upload (compresses + sends to chat-attachments bucket).
  const { state: uploadState, send: sendAttachment } = useChatAttachment(threadId ?? "");

  // 1. Chat thread state. `useChatSnapshot` synchronously hydrates
  //    from the versioned `localStorage` cache so the first render
  //    already shows history, then reconciles with the backend via
  //    `chat_snapshot_if_changed`. Anything that mutates messages
  //    or reactions (realtime, our own send, attachment insert,
  //    reaction toggle) calls `refresh()` so the hash round-trips.
  const {
    messages:  messagesRows,
    reactions: reactionsRows,
    loading:   snapshotLoading,
    refresh:   refreshSnapshot,
  } = useChatSnapshot(userId, threadId);

  // 2. Ask the browser to keep our `localStorage` cache from being
  //    evicted under disk pressure. The helper deduplicates per
  //    session, so even if the user pinches from tab to tab to the
  //    chat the browser only sees one request. The result is not
  //    used — storage eviction is best-effort.
  useEffect(() => {
    if (!userId) return;
    void requestPersistentStorage();
  }, [userId]);

  // 3. Realtime subscriptions for both tables. Either change flips
  //    the server-side hash, so we refresh the snapshot instead of
  //    refetching the tables individually. The snapshot hook handles
  //    the cross-table "new pair of hash + rows" coupling for us.
  useRealtimeTable<"chat_messages">("chat_messages", threadId ? `thread_id=eq.${threadId}` : null,
    refreshSnapshot);
  useRealtimeTable<"message_reactions">("message_reactions", threadId ? `thread_id=eq.${threadId}` : null,
    refreshSnapshot);

  // 4. Resolve signed URLs for any messages with attachments.
  //
  // Bug #12: the previous version resolved these strictly sequentially
  // (one `await` per path). For a thread with 50 images that's 50
  // round-trips — easily 5s on a slow link. Now we use the SDK's
  // `createSignedUrls(paths[], expiresIn)` batch call: one HTTP
  // request returns the full set. The SDK types each entry as
  // `{ path: string | null; signedUrl: string | null; error: ... }`
  // and entries can be null on per-path failure; we narrow with a
  // guard rather than an inline cast.
  useEffect(() => {
    let cancelled = false;
    const paths = messagesRows
      .map((m) => m.image_url)
      .filter((p): p is string => typeof p === "string" && p.length > 0);
    const fresh = paths.filter((p) => !signedUrls[p]);
    if (fresh.length === 0) return;
    (async () => {
      const { data } = await supabase.storage
        .from("chat-attachments")
        .createSignedUrls(fresh, CHAT_SIGNED_URL_TTL_SECONDS);
      if (cancelled || !Array.isArray(data)) return;
      const out: Record<string, string> = {};
      for (const entry of data) {
        if (
          entry &&
          typeof entry.path === "string" &&
          typeof entry.signedUrl === "string" &&
          entry.error === null
        ) {
          out[entry.path] = entry.signedUrl;
        }
      }
      if (Object.keys(out).length > 0) {
        setSignedUrls((prev) => ({ ...prev, ...out }));
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesRows]);


  // Build the merged Message[]. Notes on the sender-id mapping:
  //
  //   * `cf.members.find(x => x.id === m.sender_member_id)` may resolve
  //     to `undefined` while members are still loading OR if the
  //     sender row was deleted out from under us (cascade). Falling
  //     back to the literal string "you" used to mean: "I don't know
  //     who sent this — treat it as the current user so the bubble
  //     at least renders on the right side." That sounded harmless
  //     but actually mis-attributes a stranger's message to the
  //     current user (reversed alignment + read receipt shape). Now
  //     we keep the sender_member_id verbatim and only mark the row
  //     as self-aligned when we *positively* recognise the id.
  const messages: Message[] = useMemo(() => {
    const out: Message[] = [];
    for (const m of messagesRows) {
      const senderId = m.sender_member_id;
      const att = m.image_url
        ? {
            kind: kindFromMime(m.mime_type),
            url: signedUrls[m.image_url] ?? m.image_url,
            mime: m.mime_type ?? "application/octet-stream",
          }
        : undefined;
      const msg: Message = {
        id: m.id,
        from: senderId,
        body: m.body ?? undefined,
        attachment: att as AttachmentMedia | undefined,
        voice: m.voice_seconds ? { seconds: m.voice_seconds } : undefined,
        time: new Date(m.created_at).toLocaleTimeString("zh-HK", { hour: "2-digit", minute: "2-digit", hour12: false }),
        status: (m.status as "sending" | "delivered" | "read" | "failed") ?? "delivered",
      };
      // Reactions.
      const rs = reactionsRows.filter(r => r.message_id === m.id);
      const byEmoji = new Map<string, string[]>();
      for (const r of rs) {
        const member = cf.members.find(x => x.id === r.member_id);
        if (member) {
          if (!byEmoji.has(r.emoji)) byEmoji.set(r.emoji, []);
          byEmoji.get(r.emoji)!.push(member.id);
        }
      }
      const reactions = Array.from(byEmoji.entries()).map(([emoji, by]) => ({ emoji, by }));
      if (reactions.length > 0) msg.reactions = reactions;
      out.push(msg);
    }
    return out;
  }, [messagesRows, reactionsRows, signedUrls, cf.members]);

  // Auto-scroll.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Sizing: the card fills its parent via `flex-1 min-h-0`; the AppShell
  // constrains the parent's height (the column inside <main> uses
  // `flex-1` too, see AppShell.tsx) so the chat spans exactly the
  // area between the AppBar and the bottom nav. No JS measurement,
  // no race with paint, no ResizeObserver — and no way for the
  // composer to slip under the fixed bottom nav if a measurement
  // happens to be stale.

  // Mirror upload errors / completion into the visible notice.
  // MUST be before the `if (!threadId) return ...` early-return below,
  // otherwise the hook count changes between renders (first render
  // returns early with no threadId → 64 hooks; later render with
  // threadId → 65 hooks). That triggered React error #310 and
  // unmounted the entire AppShell tree.
  useEffect(() => {
    if (uploadState.phase === "error") setUploadError(uploadState.message);
    if (uploadState.phase === "done")  setUploadError(null);
  }, [uploadState]);

  if (!threadId) {
    return (
      <div className="grid h-full place-items-center p-6 text-sm text-muted-foreground">
        載入聊天室中…
      </div>
    );
  }

  const send = async () => {
    const text = draft.trim();
    if (!text || !cf.self) return;
    setDraft("");
    const { error } = await supabase.from("chat_messages").insert({
      thread_id: threadId,
      sender_member_id: cf.self.id,
      body: text,
      status: "delivered",
    });
    if (error) {
      // restore text on failure
      setDraft(text);
      setUploadError(error.message);
    } else {
      refreshSnapshot();
    }
  };
  const handleAttachmentInline = async (file: File) => {
    if (!cf.self || !threadId) return;
    setUploadError(null);
    const result = await sendAttachment(file);
    if (!result) return;
    const { error } = await supabase.from("chat_messages").insert({
      thread_id: threadId,
      sender_member_id: cf.self.id,
      image_url: result.publicUrl,
      mime_type: result.mime,
      status: "delivered",
    });
    if (error) {
      setUploadError(error.message);
    } else {
      refreshSnapshot();
    }
  };

  const toggleReaction = async (msgId: string, emoji: string) => {
    if (!cf.self) return;
    const myId = cf.self.id;
    const existing = reactionsRows.find(r => r.message_id === msgId && r.member_id === myId && r.emoji === emoji);
    if (existing) {
      await supabase.from("message_reactions")
        .delete()
        .eq("message_id", msgId)
        .eq("member_id", myId)
        .eq("emoji", emoji);
    } else {
      await supabase.from("message_reactions").insert({
        message_id: msgId, member_id: myId, emoji,
      });
    }
    refreshSnapshot();
    setShowReactionsFor(null);
  };

  return (
    <div
      className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-2xl border border-border/60 bg-card"
    >
      <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto p-3">
        {messages.length === 0 && !snapshotLoading && (
          <p className="grid h-full place-items-center text-sm text-muted-foreground">
            仲未有任何訊息 — 講聲「你好」啦 👋
          </p>
        )}
        {messages.map((m) => {
          const member = cf.members.find((x) => x.id === m.from) ?? null;
          // Self-align only when we positively recognise the sender id
          // matches `cf.self.id`. The old `|| m.from === "you"` fallback
          // was the other half of bug #5 above: if `members` hadn't
          // loaded yet, every message briefly rendered as "self".
          const self = m.from === cf.self?.id;
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className={cn("group flex items-end gap-2", self && "flex-row-reverse")}
            >
              <span
                role="img"
                aria-label={`${member?.display_name ?? (self ? (me.displayName ?? "你") : "家人")} 頭像`}
                title={member?.display_name ?? (self ? (me.displayName ?? "你") : "家人")}
                className={cn(
                  "grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br text-xs font-semibold text-white shadow-sm ring-2 ring-background",
                  member?.avatar_color ?? (self ? "from-primary to-primary/70" : "from-zinc-300 to-zinc-500"),
                )}
              >
                {self && me.avatarUrl ? (
                  <img
                    src={me.avatarUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    key={me.avatarUrl}
                  />
                ) : member?.initials ? (
                  member.initials
                ) : self ? (
                  me.avatarInitials
                ) : (
                  "·"
                )}
              </span>
              <div className={cn(
                // Column wrapper that stacks the (remote-only) name
                // above the bubble. `max-w-[78%]` lives here — NOT
                // on the inner bubble wrapper — so it caps against
                // the chat row's parent width (≈ the <main> content
                // area), not against the column's own content-driven
                // width. Moving it inside would make the constraint
                // a no-op and let long bubbles stretch the full
                // available width past the avatar.
                "flex min-w-0 max-w-[78%] flex-col gap-1",
                self && "items-end",
              )}>
                {/* Sender label above remote bubbles so the user can
                    tell at a glance who is talking. Self messages
                    don't need it — the right-aligned primary bubble
                    is unambiguously yours. Only render when we
                    positively recognise the sender id, otherwise
                    showing a blank space above an unknown sender
                    would be worse than no label at all. */}
                {!self && member && (
                  <span className="px-1 text-[11px] font-medium text-foreground/70">
                    {member.display_name}
                  </span>
                )}
                <div className="relative">
                <div
                  onDoubleClick={() => setShowReactionsFor(m.id)}
                  className={cn(
                    "rounded-2xl px-3 py-2 text-sm shadow-sm",
                    self ? "rounded-br-md bg-primary text-primary-foreground"
                         : "rounded-bl-md bg-secondary text-foreground",
                  )}
                >
                  {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
                  {m.attachment && (m.attachment.kind === "image" || m.attachment.kind === "gif") && (
                    // Wrap the <img> in a <button> so the entire
                    // thumbnail is the click target — `cursor-zoom-in`
                    // hints at the affordance. The button's
                    // accessible name comes from the inner img's
                    // `alt`, so screen readers still announce it
                    // as "相片" / "GIF" the same way they did when
                    // the img was a direct child.
                    <button
                      type="button"
                      onClick={() =>
                        setLightbox({
                          kind: m.attachment!.kind === "gif" ? "gif" : "image",
                          url: m.attachment!.url,
                          alt: m.attachment!.kind === "gif" ? "GIF" : "相片",
                          mime: m.attachment!.mime,
                        })
                      }
                      aria-label={m.attachment.kind === "gif" ? "放大 GIF" : "放大相片"}
                      className="mt-1 inline-block cursor-zoom-in overflow-hidden rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      <img
                        src={m.attachment.url}
                        alt={m.attachment.kind === "gif" ? "GIF" : "相片"}
                        className="block max-h-60 max-w-full"
                        loading="lazy"
                        draggable={false}
                      />
                    </button>
                  )}
                  {m.attachment && m.attachment.kind === "video" && (
                    // For video we keep the inline <video controls>
                    // so users can play inline at the chat size, but
                    // add a small expand overlay button in the corner
                    // that opens the lightbox. Adding `onClick` to
                    // the <video> itself would conflict with the
                    // native controls bar — the bar intercepts
                    // clicks on the play/seek area, but a click on
                    // the video frame outside the bar would also
                    // toggle playback AND open the lightbox, which
                    // is confusing. A separate overlay button keeps
                    // the two intents distinct.
                    <div className="group/video relative mt-1 inline-block overflow-hidden rounded-xl">
                      <video
                        src={m.attachment.url}
                        controls
                        preload="metadata"
                        className="block max-h-60 max-w-full"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setLightbox({
                            kind: "video",
                            url: m.attachment!.url,
                            mime: m.attachment!.mime,
                          })
                        }
                        aria-label="全螢幕播放影片"
                        className="absolute right-2 top-2 z-10 grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white shadow-md backdrop-blur-sm transition hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                      >
                        <Maximize2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  )}
                  {m.voice && (
                    <div className="flex items-center gap-2">
                      <button type="button" aria-label="播放語音"
                        className={cn("grid h-8 w-8 place-items-center rounded-full",
                          self ? "bg-primary-foreground/20" : "bg-card")}>▶</button>
                      <div className={cn("h-1 flex-1 rounded-full",
                        self ? "bg-primary-foreground/30" : "bg-foreground/15")}>
                        <div className={cn("h-full w-1/3 rounded-full",
                          self ? "bg-primary-foreground" : "bg-primary")} />
                      </div>
                      <span className="text-[10px] tabular-nums">{m.voice.seconds}s</span>
                    </div>
                  )}
                  <div className={cn("mt-1 text-[10px]",
                    self ? "text-primary-foreground/80" : "text-muted-foreground")}>
                    {m.time}
                    {m.status === "sending" && " · 傳送中…"}
                    {m.status === "failed"  && " · 傳送失敗"}
                  </div>
                </div>

                <AnimatePresence>
                  {showReactionsFor === m.id && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: 4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: 4 }}
                      transition={{ duration: 0.15 }}
                      className={cn(
                        "mt-1 flex items-center gap-0.5 rounded-full border border-border/60 bg-card px-1.5 py-0.5 shadow-sm",
                        self && "self-end",
                      )}
                    >
                      {REACTION_CHOICES.map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => toggleReaction(m.id, e)}
                          className="grid h-8 w-8 place-items-center rounded-full text-base hover:bg-secondary"
                        >
                          {e}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setShowReactionsFor(null)}
                        className="grid h-8 w-8 place-items-center rounded-full text-xs text-muted-foreground hover:bg-secondary"
                        aria-label="關閉"
                      >
                        ×
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {m.reactions && m.reactions.length > 0 && (
                  <div className={cn("mt-1 flex flex-wrap items-center gap-1", self && "justify-end")}>
                    {m.reactions.map((r) => (
                      <button
                        key={r.emoji}
                        type="button"
                        onClick={() => toggleReaction(m.id, r.emoji)}
                        className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-card px-2 py-0.5 text-[10px] text-foreground/80 hover:bg-secondary"
                      >
                        <span>{r.emoji}</span>
                        <span className="tabular-nums">{r.by.length}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            </motion.div>
          );
        })}
      </div>

      <div className="border-t border-border/60 bg-secondary/20">
        {uploadError && (
          <div role="alert" className="flex items-start gap-2 border-b border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            <span className="flex-1 leading-snug">{uploadError}</span>
            <button type="button" onClick={() => setUploadError(null)} aria-label="關閉錯誤"
              className="rounded p-0.5 text-rose-500 hover:bg-rose-100">
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        )}
        {uploadState.phase === "compressing" && (
          <div className="flex items-center gap-2 border-b border-border/40 bg-secondary/30 px-3 py-1.5 text-[11px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            正在{labelForKind(uploadState.kind)}…
          </div>
        )}
        <div className="flex items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 sm:py-2.5">
          <AttachmentTypePicker
            disabled={uploadState.phase === "compressing" || uploadState.phase === "uploading"}
            onPick={(f) => void handleAttachmentInline(f)}
          />
          <input
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              // Bug #3: don't fire `send()` while a CJK IME is composing.
              // React 18+ synthesises `keydown` events during composition
              // with `keyCode === 229` (the "composition input" sentinel)
              // and the native `isComposing` flag. The old guard
              // (`e.key === "Enter" && !e.shiftKey`) fired on every
              // Enter, which prematurely confirmed an in-progress
              // candidate (e.g. typed "ni3hao" in Cantonese → "你好"
              // would commit on Enter mid-selection).
              if (e.key !== "Enter" || e.shiftKey) return;
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              e.preventDefault();
              void send();
            }}
            placeholder="傳送訊息給家人…"
            className="h-11 min-w-0 flex-1 rounded-full border border-border/60 bg-background px-4 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
          <motion.button
            type="button"
            onClick={() => void send()}
            disabled={!draft.trim()}
            whileTap={draft.trim() ? { scale: 0.92 } : undefined}
            whileHover={draft.trim() ? { scale: 1.04 } : undefined}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
            aria-label="送出訊息"
            className={cn(
              "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-sm transition-colors",
              draft.trim() ? "bg-primary text-primary-foreground shadow-primary/30"
                             : "bg-secondary/60 text-foreground/40",
            )}
          >
            <Send className="h-4.5 w-4.5" aria-hidden />
          </motion.button>
        </div>
      </div>
      {/* Fullscreen preview for images / GIFs / videos. Lives at the
          end of the card so it doesn't interfere with the message
          list or composer; the lightbox itself is `position: fixed`
          so the parent layout is irrelevant. */}
      <MediaLightbox media={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function kindFromMime(mime: string | null): "image" | "video" | "gif" {
  if (!mime) return "image";
  if (mime === "image/gif") return "gif";
  if (mime.startsWith("video/")) return "video";
  return "image";
}

export default FamilyChatView;
